import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { BsArrowUp, BsArrowDown } from 'react-icons/bs';

type Props = {
  joinedData: any[];
  title?: string; // Optional title prop
};

const CustomReportTable = (props: Props) => {
  const { joinedData, title } = props;

  // State for sorting only
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

  // Helper function to find benchmark column name
  const getBenchmarkColumnName = (data: any[]) => {
    if (data.length === 0) return null;
    return Object.keys(data[0]).find((key) => key.includes(' benchmark')) || null;
  };

  // Helper function to calculate rowSpan for benchmark column
  const calculateBenchmarkRowSpans = (data: any[]) => {
    const rowSpans: { [key: number]: number } = {};
    const skipRows: Set<number> = new Set();

    if (data.length === 0) return { rowSpans, skipRows };

    const benchmarkColumnName = getBenchmarkColumnName(data);
    if (!benchmarkColumnName) return { rowSpans, skipRows };

    // Get dimension columns (string values) excluding specific ones
    const dimensionColumns = Object.keys(data[0]).filter(
      (key) => typeof data[0][key] === 'string' && key !== 'lineitem' && key !== 'campaign_id',
    );

    for (let i = 0; i < data.length; i++) {
      if (skipRows.has(i)) continue;

      const currentRow = data[i];
      const benchmarkValue = currentRow[benchmarkColumnName];

      if (benchmarkValue === undefined || benchmarkValue === '-') {
        rowSpans[i] = 1;
        continue;
      }

      // Count consecutive rows with same dimension values and benchmark value
      let spanCount = 1;
      for (let j = i + 1; j < data.length; j++) {
        const nextRow = data[j];

        // Check if all dimension columns match
        const dimensionsMatch = dimensionColumns.every((col) => nextRow[col] === currentRow[col]);

        if (dimensionsMatch && nextRow[benchmarkColumnName] === benchmarkValue) {
          spanCount++;
          skipRows.add(j);
        } else {
          break;
        }
      }

      rowSpans[i] = spanCount;
    }

    return { rowSpans, skipRows };
  };

  // Detect columns and prepare data
  const { columns, data, footerRow } = useMemo(() => {
    // Skip if no data
    if (!joinedData || joinedData.length === 0) {
      return { columns: [], data: [], footerRow: null };
    }

    // Find the footer row (assumed to be the last row with campaign_id)
    const lastRowIndex = joinedData.length - 1;
    const hasFooterRow = joinedData[lastRowIndex].campaign_id !== undefined;

    // Separate footer row from regular data
    const regularData = hasFooterRow ? joinedData.slice(0, lastRowIndex) : joinedData;
    const footer = hasFooterRow ? joinedData[lastRowIndex] : null;

    // Extract all unique columns from data
    const allColumns = new Set<string>();
    regularData.forEach((row) => {
      Object.keys(row).forEach((key) => allColumns.add(key));
    });

    // If there's a footer, add its keys too
    if (footer) {
      Object.keys(footer).forEach((key) => allColumns.add(key));
    }

    // Convert Set to Array, keeping original column names, excluding campaign_id
    const columnArray = Array.from(allColumns)
      .filter((col) => col !== 'campaign_id')
      .map((col) => ({
        key: col,
        label: col, // Keep original column name
      }));

    return {
      columns: columnArray,
      data: regularData,
      footerRow: footer,
    };
  }, [joinedData]);

  // Apply sorting only
  const processedData = useMemo(() => {
    let sortedData = [...data];

    // Apply sorting
    if (sortConfig) {
      sortedData.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }

        const aString = String(aValue).toLowerCase();
        const bString = String(bValue).toLowerCase();

        if (aString < bString) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aString > bString) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return sortedData;
  }, [data, sortConfig]);

  // Handle sorting - cycles through: no sort → asc → desc → no sort
  const handleSort = (key: string) => {
    if (!sortConfig || sortConfig.key !== key) {
      // First click on new column: ascending
      setSortConfig({ key, direction: 'asc' });
    } else if (sortConfig.direction === 'asc') {
      // Second click: descending
      setSortConfig({ key, direction: 'desc' });
    } else {
      // Third click: reset to no sort
      setSortConfig(null);
    }
  };

  // Render table or placeholder
  if (!columns.length) {
    return <EmptyState>No data available</EmptyState>;
  }

  return (
    <TableContainer>
      <h1 className="title">{title}</h1>
      <TableWrapper>
        <Table>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key}>
                  <HeaderContent>
                    <div onClick={() => handleSort(column.key)}>
                      {column.label}
                      {sortConfig?.key === column.key ? (
                        <SortIcon>{sortConfig.direction === 'asc' ? <BsArrowUp /> : <BsArrowDown />}</SortIcon>
                      ) : null}
                    </div>
                  </HeaderContent>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(() => {
              const { rowSpans, skipRows } = calculateBenchmarkRowSpans(processedData);
              const benchmarkColumnName = getBenchmarkColumnName(processedData);

              return processedData.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  {columns.map((column) => {
                    // Handle benchmark column with merged cells
                    if (benchmarkColumnName && column.key === benchmarkColumnName) {
                      if (skipRows.has(rowIndex)) {
                        return null; // Skip this cell, it's merged with above
                      }

                      const rowSpan = rowSpans[rowIndex] || 1;
                      return (
                        <td
                          key={column.key}
                          rowSpan={rowSpan > 1 ? rowSpan : undefined}
                          style={{
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            // backgroundColor: rowSpan > 1 ? '#f8f9fa' : undefined,
                          }}
                        >
                          {row[column.key] !== undefined
                            ? typeof row[column.key] === 'number'
                              ? Number(row[column.key]).toLocaleString(undefined, {})
                              : row[column.key]
                            : ''}
                        </td>
                      );
                    }

                    // Handle regular columns
                    return (
                      <td key={column.key}>
                        {row[column.key] !== undefined
                          ? typeof row[column.key] === 'number'
                            ? Number(row[column.key]).toLocaleString(undefined, {})
                            : row[column.key]
                          : ''}
                      </td>
                    );
                  })}
                </TableRow>
              ));
            })()}
          </tbody>
          {footerRow && (
            <tfoot>
              <tr>
                {columns.map((column) => (
                  <td key={column.key}>
                    {footerRow[column.key] !== undefined
                      ? typeof footerRow[column.key] === 'number'
                        ? Number(footerRow[column.key]).toLocaleString(undefined, {})
                        : footerRow[column.key]
                      : ''}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </Table>
      </TableWrapper>
    </TableContainer>
  );
};

// Styled components
const TableContainer = styled.div`
  width: 100%;
  height: 100%;
  margin: 0 auto;
  padding: 10px;
  background: white;
  border-radius: 5px;
  /* box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12); */
  font-size: 10px;
  display: flex;
  flex-direction: column;

  .title {
    font-size: 14px;
    margin: 0 0 10px 0;
    padding-left: 10px;
    text-align: left;
    font-family: 'fontRegular';
    flex-shrink: 0; /* Naslov neće se smanjiti */
  }
`;

const TableWrapper = styled.div`
  width: 100%;
  flex: 1;
  overflow: auto;
  min-height: 0; /* Ključno za flexbox scrolling */
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 12px 15px;
    text-align: left;
    border-bottom: 1px solid #f0f0f0;
  }

  th {
    background-color: #f8f9fa;
    color: #495057;
    font-weight: 600;
    position: sticky;
    top: 0;
    z-index: 10;
    border-bottom: 1px solid #e9ecef;
  }

  tbody tr:hover {
    background-color: rgba(0, 102, 204, 0.03);
  }

  tfoot tr {
    font-weight: bold;
    background-color: #f8f9fa;
  }
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    /* background-color: #f8f9fa; */
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;

  > div {
    cursor: pointer;
  }
`;

const SortIcon = styled.span`
  margin-left: 5px;
  display: inline-flex;
  align-items: center;
`;

const EmptyState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #6c757d;
  font-size: 16px;
`;

export default CustomReportTable;
